/**
 * Interface for elastic search UI client settings
 * Mirrors the localStorage structure currently used in elastic-search.component.ts
 */
export interface IUiClientSettingsElasticSearch {
  /** Search options configuration (tabs enabled/active) */
  SearchOptions: Array<{
    Type: string;
    Enabled: boolean;
    TabActive: boolean;
  }>;
  /** Person action toggles configuration */
  PersonActions: Array<{
    Id: string;
    Enabled: boolean;
  }>;
}

/**
 * Interface for sidebar UI client settings
 * Mirrors the localStorage structure currently used in navbar.store.ts
 */
export interface IUiClientSettingsSidebar {
  /** Whether the sidebar is pinned */
  Pinned: boolean;
  /** Sidebar width in pixels (clamped between 200-400) */
  Width: number;
}

/**
 * Main interface for all UI client settings
 * This is stored as a JSON blob in the UserSettings.UiClientSettings field
 */
export interface IUiClientSettings {
  /** Elastic search preferences */
  ElasticSearch?: IUiClientSettingsElasticSearch;
  /** Sidebar preferences */
  Sidebar?: IUiClientSettingsSidebar;
  /** Optional: core shortcuts preferences for future use */
  Shortcuts?: {
    /** Ordering or visibility overrides for shortcuts */
    Ordering?: string[];
    /** Hidden shortcuts */
    Hidden?: string[];
  };
}

/** Default values for sidebar settings */
export const SidebarMinWidth = 200;
export const SidebarMaxWidth = 400;
export const SidebarDefaultWidth = 253;

/** Search option types matching elastic-search.model.ts (first is active by default) */
const DEFAULT_SEARCH_TYPES = ['All', 'Customer', 'Visitor', 'User', 'Action'] as const;

/** Default elastic search search options (PascalCase storage format) */
const DefaultSearchOptions: IUiClientSettingsElasticSearch['SearchOptions'] =
  DEFAULT_SEARCH_TYPES.map((type, index) => ({
    Type: type,
    Enabled: true,
    TabActive: index === 0,
  }));

/** Person action ids matching elastic-search.model.ts */
const DEFAULT_PERSON_ACTION_IDS = [
  'EDIT_INFO',
  'OPEN_WHATSAPP',
  'OPEN_CHAT',
  'OPEN_ACCESS_EDGES',
  'NEW_SUBSCRIPTION',
  'NEW_SALE',
  'NEW_SERVICE',
  'NEW_RECEIPT',
] as const;

/** Default person actions (PascalCase storage format) */
const DefaultPersonActions: IUiClientSettingsElasticSearch['PersonActions'] =
  DEFAULT_PERSON_ACTION_IDS.map((id) => ({
    Id: id,
    Enabled: true,
  }));

/** Default UI client settings used when API returns null/undefined or on GET failure */
export const DefaultUiClientSettings: IUiClientSettings = {
  ElasticSearch: {
    SearchOptions: DefaultSearchOptions,
    PersonActions: DefaultPersonActions,
  },
  Sidebar: {
    Pinned: false,
    Width: SidebarDefaultWidth,
  },
};

/**
 * Parses UiClientSettings from API response
 * Handles both string (JSON) and object formats
 * Falls back to defaults on parsing error
 */
export function parseUiClientSettings(
  value: string | IUiClientSettings | null | undefined
): IUiClientSettings {
  if (!value) {
    return DefaultUiClientSettings;
  }

  try {
    const parsed =
      typeof value === 'string' ? JSON.parse(value) : value;

    const es = parsed.ElasticSearch ?? parsed.elasticSearch;
    const sidebar = parsed.Sidebar ?? parsed.sidebar;
    const shortcuts = parsed.Shortcuts ?? parsed.coreShortcuts;

    return {
      ElasticSearch: {
        SearchOptions:
          es?.SearchOptions ?? es?.searchOptions ?? DefaultUiClientSettings.ElasticSearch!.SearchOptions,
        PersonActions:
          es?.PersonActions ?? es?.personActions ?? DefaultUiClientSettings.ElasticSearch!.PersonActions,
      },
      Sidebar: {
        Pinned:
          sidebar?.Pinned ?? sidebar?.pinned ?? DefaultUiClientSettings.Sidebar!.Pinned,
        Width: clampSidebarWidth(
          sidebar?.Width ?? sidebar?.width ?? DefaultUiClientSettings.Sidebar!.Width
        ),
      },
      Shortcuts: shortcuts ?? undefined,
    };
  } catch (error) {
    console.warn('Failed to parse UiClientSettings, using defaults:', error);
    return DefaultUiClientSettings;
  }
}

/**
 * Clamps sidebar width to valid range
 */
export function clampSidebarWidth(width: number): number {
  return Math.max(SidebarMinWidth, Math.min(SidebarMaxWidth, width));
}

/**
 * Merges partial UiClientSettings update with existing settings
 */
export function mergeUiClientSettings(
  existing: IUiClientSettings,
  partial: Partial<IUiClientSettings>
): IUiClientSettings {
  const partialEs = partial.ElasticSearch;
  const partialSidebar = partial.Sidebar;

  return {
    ElasticSearch: partialEs
      ? {
          SearchOptions:
            partialEs.SearchOptions ??
            existing.ElasticSearch?.SearchOptions ??
            DefaultUiClientSettings.ElasticSearch!.SearchOptions,
          PersonActions:
            partialEs.PersonActions ??
            existing.ElasticSearch?.PersonActions ??
            DefaultUiClientSettings.ElasticSearch!.PersonActions,
        }
      : existing.ElasticSearch,
    Sidebar: partialSidebar
      ? {
          Pinned:
            partialSidebar.Pinned ??
            existing.Sidebar?.Pinned ??
            DefaultUiClientSettings.Sidebar!.Pinned,
          Width: clampSidebarWidth(
            partialSidebar.Width ??
              existing.Sidebar?.Width ??
              DefaultUiClientSettings.Sidebar!.Width
          ),
        }
      : existing.Sidebar,
    Shortcuts: partial.Shortcuts ?? existing.Shortcuts,
  };
}

/**
 * Local interface matching ISearchOption structure from elastic-search.model.ts
 */
interface ISearchOptionLocal {
  type: string;
  enabled: boolean;
  tabActive: boolean;
  [key: string]: any;
}

/**
 * Local interface matching IPersonActionOption structure from elastic-search.model.ts
 */
interface IPersonActionOptionLocal {
  id: string;
  enabled: boolean;
  [key: string]: any;
}

/**
 * Converts elastic search options to storage format (PascalCase)
 */
export function searchOptionsToStorageFormat(
  options: ISearchOptionLocal[]
): IUiClientSettingsElasticSearch['SearchOptions'] {
  return options.map((option) => ({
    Type: option.type,
    Enabled: option.enabled,
    TabActive: option.tabActive,
  }));
}

/**
 * Converts person actions to storage format (PascalCase)
 */
export function personActionsToStorageFormat(
  actions: IPersonActionOptionLocal[]
): IUiClientSettingsElasticSearch['PersonActions'] {
  return actions.map((action) => ({
    Id: action.id,
    Enabled: action.enabled,
  }));
}

/**
 * Converts storage format search options (PascalCase) to full search options (camelCase for UI)
 */
export function storageFormatToSearchOptions(
  storedOptions: IUiClientSettingsElasticSearch['SearchOptions'] | undefined,
  defaultOptions: ISearchOptionLocal[]
): ISearchOptionLocal[] {
  if (!storedOptions || storedOptions.length === 0) {
    return defaultOptions;
  }

  return defaultOptions.map((defaultOption) => {
    const stored = storedOptions.find((opt) => opt.Type === defaultOption.type);
    return stored
      ? {
          ...defaultOption,
          enabled: stored.Enabled,
          tabActive: stored.TabActive,
        }
      : defaultOption;
  });
}

/**
 * Converts storage format person actions (PascalCase) to full person actions (camelCase for UI)
 */
export function storageFormatToPersonActions(
  storedActions: IUiClientSettingsElasticSearch['PersonActions'] | undefined,
  defaultActions: IPersonActionOptionLocal[]
): IPersonActionOptionLocal[] {
  if (!storedActions || storedActions.length === 0) {
    return defaultActions;
  }

  return defaultActions.map((defaultAction) => {
    const stored = storedActions.find((action) => action.Id === defaultAction.id);
    return stored
      ? { ...defaultAction, enabled: stored.Enabled }
      : defaultAction;
  });
}
